import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Check, Phone, ZoomIn } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import Lightbox from '../components/Lightbox';
import { useLanguage } from '../i18n/pageTranslations';

const CirugiaAbdomen = () => {
  const lang = useLanguage();
  const [lbIndex, setLbIndex] = useState(null);
  const [lbImages, setLbImages] = useState([]);
  const openLb = (images, index) => { setLbImages(images); setLbIndex(index); };
  
  const t = {
    es: {
      title: 'Abdominoplastia',
      heroP1: '<strong>Cirugía de abdomen</strong> para eliminar el exceso de piel y grasa, consiguiendo un vientre plano y firme.',
      heroP2: 'Resultados naturales que devuelven la confianza y mejoran la silueta corporal.',
      cta: 'SOLICITAR CONSULTA',
      resultsTitle: 'Resultados de Abdominoplastia',
      resultsSubtitle: 'Transformación real del contorno abdominal',
      case1Title: 'Caso 1: Abdominoplastia con Liposucción',
      case1Desc: 'Combinación de técnicas para un resultado integral y natural.',
      before: 'Antes',
      after: 'Después',
      disclaimer: 'Caso real - Resultados pueden variar según cada paciente',
      days30: '30 días',
      days90: '90 días',
      scar90: 'Cicatriz 90 días',
      neoumbilico: 'Neoumbilicoplastia',
      relaxed90: '90 días - Musculatura relajada',
      finalResult: 'Resultado final',
      diastasisNote: 'Durante la intervención se realiza un refuerzo de la pared muscular que con los embarazos suele abrirse en la línea media (corrección de diástasis).',
      case2Title: 'Caso 2: Abdominoplastia + Liposucción + Dieta',
      case2Desc: 'Transformación completa combinando cirugía con cambios en el estilo de vida. Resultado al cabo de un año.',
      oneYear: '1 año después',
      scarYear: 'Cicatriz al año',
      case3Title: 'Caso 3: Abdominoplastia + Mamoplastia Combinadas',
      case3Desc: 'Cirugía combinada con ligera marcación de abdominales. Paciente de 67 años.',
      case3Note: 'Resultado un mes después de la intervención.',
      oneMonth: '1 mes después',
      whatIsTitle: '¿Qué es la Abdominoplastia?',
      whatIsP1: 'La <strong>abdominoplastia</strong> o dermolipectomía abdominal es una intervención quirúrgica que permite eliminar el exceso de piel y grasa del abdomen, además de reparar los músculos de la pared abdominal que pueden haberse separado o debilitado.',
      whatIsP2: 'Es especialmente indicada para personas que han experimentado cambios significativos de peso, mujeres después del embarazo, o cuando la piel ha perdido elasticidad y no responde al ejercicio ni a la dieta.',
      wallTitle: 'Cirugía de Pared Abdominal',
      wallP1: 'La abdominoplastia frecuentemente se combina con la <strong>reparación de la pared abdominal</strong>, incluyendo la corrección de diástasis de rectos y hernias cuando es necesario. Esta combinación permite obtener resultados tanto estéticos como funcionales en una sola intervención.',
      diastasisTitle: 'Corrección de Diástasis de Rectos',
      diastasisP1: 'La <strong>diástasis de rectos</strong> es la separación de los músculos rectos del abdomen, común después del embarazo o por aumento de peso significativo. Esta separación causa:',
      diastasisList: ['Abultamiento abdominal que no mejora con ejercicio', 'Debilidad de la pared abdominal', 'Posibles molestias de espalda', 'Dificultad para realizar actividades físicas'],
      diastasisP2: 'Durante la abdominoplastia, los músculos se suturan en la línea media, restaurando la función y el aspecto del abdomen.',
      herniaTitle: 'Reparación de Hernias',
      herniaP1: 'Cuando existe una <strong>hernia abdominal</strong> (umbilical, epigástrica o incisional), esta puede repararse durante el mismo procedimiento de abdominoplastia, aprovechando el acceso quirúrgico y evitando una segunda intervención.',
      herniaP2: 'La reparación se realiza reforzando la pared abdominal, lo que proporciona un resultado más seguro y duradero.',
      typesTitle: 'Tipos de Abdominoplastia',
      typeComplete: 'Abdominoplastia Completa',
      typeCompleteDesc: 'Indicada cuando hay un exceso significativo de piel y grasa. Se realiza una incisión de cadera a cadera, se elimina el exceso de tejido y se reposiciona el ombligo.',
      typeMini: 'Miniabdominoplastia',
      typeMiniDesc: 'Para casos con menor exceso de piel, principalmente en la zona inferior del abdomen. La cicatriz es más corta y la recuperación más rápida.',
      typeLipo: 'Abdominoplastia con Liposucción',
      typeLipoDesc: 'Combina la eliminación de piel con la extracción de grasa localizada para conseguir un contorno más definido.',
      benefitsTitle: 'Beneficios de la Abdominoplastia',
      benefit1: 'Vientre Plano',
      benefit1Desc: 'Eliminación del exceso de piel y grasa para un abdomen firme y tonificado.',
      benefit2: 'Mejora Postural',
      benefit2Desc: 'La reparación de los músculos abdominales mejora la postura y reduce molestias de espalda.',
      benefit3: 'Resultados Duraderos',
      benefit3Desc: 'Con un estilo de vida saludable, los resultados se mantienen a largo plazo.',
      benefit4: 'Mayor Confianza',
      benefit4Desc: 'Mejora significativa de la autoestima y la imagen corporal.',
      expTitle: 'Experiencia en Cirugía Corporal',
      expP1: 'El Dr. Ángel Norro cuenta con más de <strong>30 años de experiencia</strong> en cirugía estética corporal, realizando cientos de abdominoplastias con excelentes resultados.',
      expP2: 'Cada paciente recibe un <strong>estudio personalizado</strong> para determinar la técnica más adecuada según sus características y objetivos.',
      expP3: 'El objetivo es conseguir un resultado <strong>natural y armonioso</strong> que mejore la silueta respetando las proporciones de cada cuerpo.',
      contactTitle: '¿Interesada en la Abdominoplastia?',
      contactDesc: 'Solicite una valoración personalizada',
    },
    en: {
      title: 'Abdominoplasty',
      heroP1: '<strong>Abdomen surgery</strong> to remove excess skin and fat, achieving a flat and firm stomach.',
      heroP2: 'Natural results that restore confidence and improve body silhouette.',
      cta: 'REQUEST CONSULTATION',
      resultsTitle: 'Abdominoplasty Results',
      resultsSubtitle: 'Real transformation of abdominal contour',
      case1Title: 'Case 1: Abdominoplasty with Liposuction',
      case1Desc: 'Combination of techniques for a comprehensive and natural result.',
      before: 'Before',
      after: 'After',
      disclaimer: 'Real case - Results may vary for each patient',
      days30: '30 days',
      days90: '90 days',
      scar90: 'Scar at 90 days',
      neoumbilico: 'Neoumbilicoplasty',
      relaxed90: '90 days - Relaxed muscles',
      finalResult: 'Final result',
      diastasisNote: 'During the intervention, reinforcement of the muscle wall is performed, which often opens in the midline with pregnancies (diastasis correction).',
      case2Title: 'Case 2: Abdominoplasty + Liposuction + Diet',
      case2Desc: 'Complete transformation combining surgery with lifestyle changes. Result after one year.',
      oneYear: '1 year later',
      scarYear: 'Scar at one year',
      case3Title: 'Case 3: Combined Abdominoplasty + Mammoplasty',
      case3Desc: 'Combined surgery with slight abdominal marking. 67-year-old patient.',
      case3Note: 'Result one month after the intervention.',
      oneMonth: '1 month later',
      whatIsTitle: 'What is Abdominoplasty?',
      whatIsP1: '<strong>Abdominoplasty</strong> or abdominal dermolipectomy is a surgical intervention that removes excess skin and fat from the abdomen, as well as repairing abdominal wall muscles that may have separated or weakened.',
      whatIsP2: 'It is especially indicated for people who have experienced significant weight changes, women after pregnancy, or when the skin has lost elasticity and does not respond to exercise or diet.',
      wallTitle: 'Abdominal Wall Surgery',
      wallP1: 'Abdominoplasty is often combined with <strong>abdominal wall repair</strong>, including correction of diastasis recti and hernias when necessary. This combination allows for both aesthetic and functional results in a single intervention.',
      diastasisTitle: 'Diastasis Recti Correction',
      diastasisP1: '<strong>Diastasis recti</strong> is the separation of the rectus abdominis muscles, common after pregnancy or significant weight gain. This separation causes:',
      diastasisList: ['Abdominal bulging that does not improve with exercise', 'Weakness of the abdominal wall', 'Possible back discomfort', 'Difficulty performing physical activities'],
      diastasisP2: 'During abdominoplasty, the muscles are sutured in the midline, restoring the function and appearance of the abdomen.',
      herniaTitle: 'Hernia Repair',
      herniaP1: 'When an <strong>abdominal hernia</strong> (umbilical, epigastric or incisional) exists, it can be repaired during the same abdominoplasty procedure, taking advantage of surgical access and avoiding a second intervention.',
      herniaP2: 'The repair is done by reinforcing the abdominal wall, which provides a safer and longer-lasting result.',
      typesTitle: 'Types of Abdominoplasty',
      typeComplete: 'Complete Abdominoplasty',
      typeCompleteDesc: 'Indicated when there is significant excess skin and fat. An incision is made from hip to hip, excess tissue is removed and the navel is repositioned.',
      typeMini: 'Mini Abdominoplasty',
      typeMiniDesc: 'For cases with less excess skin, mainly in the lower abdomen area. The scar is shorter and recovery faster.',
      typeLipo: 'Abdominoplasty with Liposuction',
      typeLipoDesc: 'Combines skin removal with localized fat extraction to achieve a more defined contour.',
      benefitsTitle: 'Benefits of Abdominoplasty',
      benefit1: 'Flat Stomach',
      benefit1Desc: 'Removal of excess skin and fat for a firm and toned abdomen.',
      benefit2: 'Improved Posture',
      benefit2Desc: 'Repair of abdominal muscles improves posture and reduces back discomfort.',
      benefit3: 'Lasting Results',
      benefit3Desc: 'With a healthy lifestyle, results are maintained long-term.',
      benefit4: 'Greater Confidence',
      benefit4Desc: 'Significant improvement in self-esteem and body image.',
      expTitle: 'Experience in Body Surgery',
      expP1: 'Dr. Ángel Norro has more than <strong>30 years of experience</strong> in body aesthetic surgery, performing hundreds of abdominoplasties with excellent results.',
      expP2: 'Each patient receives a <strong>personalized study</strong> to determine the most appropriate technique according to their characteristics and goals.',
      expP3: 'The goal is to achieve a <strong>natural and harmonious</strong> result that improves the silhouette while respecting the proportions of each body.',
      contactTitle: 'Interested in Abdominoplasty?',
      contactDesc: 'Request a personalized assessment',
    },
    fr: {
      title: 'Abdominoplastie',
      heroP1: '<strong>Chirurgie abdominale</strong> pour éliminer l\'excès de peau et de graisse, obtenant un ventre plat et ferme.',
      heroP2: 'Résultats naturels qui restaurent la confiance et améliorent la silhouette corporelle.',
      cta: 'DEMANDER UNE CONSULTATION',
      resultsTitle: 'Résultats d\'Abdominoplastie',
      resultsSubtitle: 'Transformation réelle du contour abdominal',
      case1Title: 'Cas 1: Abdominoplastie avec Liposuccion',
      case1Desc: 'Combinaison de techniques pour un résultat complet et naturel.',
      before: 'Avant',
      after: 'Après',
      disclaimer: 'Cas réel - Les résultats peuvent varier selon chaque patient',
      days30: '30 jours',
      days90: '90 jours',
      scar90: 'Cicatrice à 90 jours',
      neoumbilico: 'Néo-ombilicoplastie',
      relaxed90: '90 jours - Musculature détendue',
      finalResult: 'Résultat final',
      diastasisNote: 'Pendant l\'intervention, un renforcement de la paroi musculaire est effectué, qui s\'ouvre souvent sur la ligne médiane avec les grossesses (correction du diastasis).',
      case2Title: 'Cas 2: Abdominoplastie + Liposuccion + Régime',
      case2Desc: 'Transformation complète combinant chirurgie et changements de mode de vie. Résultat après un an.',
      oneYear: '1 an après',
      scarYear: 'Cicatrice à un an',
      case3Title: 'Cas 3: Abdominoplastie + Mammoplastie Combinées',
      case3Desc: 'Chirurgie combinée avec légère définition abdominale. Patiente de 67 ans.',
      case3Note: 'Résultat un mois après l\'intervention.',
      oneMonth: '1 mois après',
      whatIsTitle: 'Qu\'est-ce que l\'Abdominoplastie?',
      whatIsP1: 'L\'<strong>abdominoplastie</strong> ou dermolipectomie abdominale est une intervention chirurgicale qui permet d\'éliminer l\'excès de peau et de graisse de l\'abdomen, ainsi que de réparer les muscles de la paroi abdominale qui peuvent s\'être séparés ou affaiblis.',
      whatIsP2: 'Elle est particulièrement indiquée pour les personnes ayant connu des changements de poids significatifs, les femmes après la grossesse, ou lorsque la peau a perdu son élasticité et ne répond pas à l\'exercice ni au régime.',
      wallTitle: 'Chirurgie de la Paroi Abdominale',
      wallP1: 'L\'abdominoplastie est souvent combinée avec la <strong>réparation de la paroi abdominale</strong>, incluant la correction du diastasis des grands droits et des hernies si nécessaire. Cette combinaison permet d\'obtenir des résultats tant esthétiques que fonctionnels en une seule intervention.',
      diastasisTitle: 'Correction du Diastasis des Grands Droits',
      diastasisP1: 'Le <strong>diastasis des grands droits</strong> est la séparation des muscles grands droits de l\'abdomen, fréquent après la grossesse ou une prise de poids importante. Cette séparation provoque:',
      diastasisList: ['Gonflement abdominal qui ne s\'améliore pas avec l\'exercice', 'Faiblesse de la paroi abdominale', 'Possibles douleurs dorsales', 'Difficulté à réaliser des activités physiques'],
      diastasisP2: 'Pendant l\'abdominoplastie, les muscles sont suturés sur la ligne médiane, restaurant la fonction et l\'apparence de l\'abdomen.',
      herniaTitle: 'Réparation des Hernies',
      herniaP1: 'Lorsqu\'une <strong>hernie abdominale</strong> (ombilicale, épigastrique ou incisionnelle) existe, elle peut être réparée pendant la même procédure d\'abdominoplastie, profitant de l\'accès chirurgical et évitant une seconde intervention.',
      herniaP2: 'La réparation se fait en renforçant la paroi abdominale, ce qui fournit un résultat plus sûr et durable.',
      typesTitle: 'Types d\'Abdominoplastie',
      typeComplete: 'Abdominoplastie Complète',
      typeCompleteDesc: 'Indiquée lorsqu\'il y a un excès significatif de peau et de graisse. Une incision est faite de hanche à hanche, l\'excès de tissu est retiré et le nombril est repositionné.',
      typeMini: 'Mini Abdominoplastie',
      typeMiniDesc: 'Pour les cas avec moins d\'excès de peau, principalement dans la zone abdominale inférieure. La cicatrice est plus courte et la récupération plus rapide.',
      typeLipo: 'Abdominoplastie avec Liposuccion',
      typeLipoDesc: 'Combine l\'élimination de la peau avec l\'extraction de graisse localisée pour obtenir un contour plus défini.',
      benefitsTitle: 'Avantages de l\'Abdominoplastie',
      benefit1: 'Ventre Plat',
      benefit1Desc: 'Élimination de l\'excès de peau et de graisse pour un abdomen ferme et tonique.',
      benefit2: 'Amélioration Posturale',
      benefit2Desc: 'La réparation des muscles abdominaux améliore la posture et réduit les douleurs dorsales.',
      benefit3: 'Résultats Durables',
      benefit3Desc: 'Avec un mode de vie sain, les résultats sont maintenus à long terme.',
      benefit4: 'Plus de Confiance',
      benefit4Desc: 'Amélioration significative de l\'estime de soi et de l\'image corporelle.',
      expTitle: 'Expérience en Chirurgie Corporelle',
      expP1: 'Le Dr. Ángel Norro compte plus de <strong>30 ans d\'expérience</strong> en chirurgie esthétique corporelle, réalisant des centaines d\'abdominoplasties avec d\'excellents résultats.',
      expP2: 'Chaque patient reçoit une <strong>étude personnalisée</strong> pour déterminer la technique la plus appropriée selon ses caractéristiques et objectifs.',
      expP3: 'L\'objectif est d\'obtenir un résultat <strong>naturel et harmonieux</strong> qui améliore la silhouette tout en respectant les proportions de chaque corps.',
      contactTitle: 'Intéressée par l\'Abdominoplastie?',
      contactDesc: 'Demandez une évaluation personnalisée',
    },
    pt: {
      title: 'Abdominoplastia',
      heroP1: '<strong>Cirurgia de abdômen</strong> para eliminar o excesso de pele e gordura, conseguindo uma barriga plana e firme.',
      heroP2: 'Resultados naturais que devolvem a confiança e melhoram a silhueta corporal.',
      cta: 'SOLICITAR CONSULTA',
      resultsTitle: 'Resultados de Abdominoplastia',
      resultsSubtitle: 'Transformação real do contorno abdominal',
      case1Title: 'Caso 1: Abdominoplastia com Lipoaspiração',
      case1Desc: 'Combinação de técnicas para um resultado integral e natural.',
      before: 'Antes',
      after: 'Depois',
      disclaimer: 'Caso real - Resultados podem variar segundo cada paciente',
      days30: '30 dias',
      days90: '90 dias',
      scar90: 'Cicatriz aos 90 dias',
      neoumbilico: 'Neoumbilicoplastia',
      relaxed90: '90 dias - Musculatura relaxada',
      finalResult: 'Resultado final',
      diastasisNote: 'Durante a intervenção é realizado um reforço da parede muscular que com as gestações costuma abrir-se na linha média (correção de diástase).',
      case2Title: 'Caso 2: Abdominoplastia + Lipoaspiração + Dieta',
      case2Desc: 'Transformação completa combinando cirurgia com mudanças no estilo de vida. Resultado após um ano.',
      oneYear: '1 ano depois',
      scarYear: 'Cicatriz ao ano',
      case3Title: 'Caso 3: Abdominoplastia + Mamoplastia Combinadas',
      case3Desc: 'Cirurgia combinada com leve marcação de abdominais. Paciente de 67 anos.',
      case3Note: 'Resultado um mês após a intervenção.',
      oneMonth: '1 mês depois',
      whatIsTitle: 'O que é Abdominoplastia?',
      whatIsP1: 'A <strong>abdominoplastia</strong> ou dermolipectomia abdominal é uma intervenção cirúrgica que permite eliminar o excesso de pele e gordura do abdômen, além de reparar os músculos da parede abdominal que podem ter se separado ou enfraquecido.',
      whatIsP2: 'É especialmente indicada para pessoas que experimentaram mudanças significativas de peso, mulheres após a gravidez, ou quando a pele perdeu elasticidade e não responde ao exercício nem à dieta.',
      wallTitle: 'Cirurgia de Parede Abdominal',
      wallP1: 'A abdominoplastia frequentemente é combinada com a <strong>reparação da parede abdominal</strong>, incluindo a correção de diástase dos retos e hérnias quando necessário. Esta combinação permite obter resultados tanto estéticos quanto funcionais em uma única intervenção.',
      diastasisTitle: 'Correção de Diástase dos Retos',
      diastasisP1: 'A <strong>diástase dos retos</strong> é a separação dos músculos retos do abdômen, comum após a gravidez ou por aumento de peso significativo. Esta separação causa:',
      diastasisList: ['Protuberância abdominal que não melhora com exercício', 'Fraqueza da parede abdominal', 'Possíveis dores nas costas', 'Dificuldade para realizar atividades físicas'],
      diastasisP2: 'Durante a abdominoplastia, os músculos são suturados na linha média, restaurando a função e a aparência do abdômen.',
      herniaTitle: 'Reparação de Hérnias',
      herniaP1: 'Quando existe uma <strong>hérnia abdominal</strong> (umbilical, epigástrica ou incisional), esta pode ser reparada durante o mesmo procedimento de abdominoplastia, aproveitando o acesso cirúrgico e evitando uma segunda intervenção.',
      herniaP2: 'A reparação é realizada reforçando a parede abdominal, o que proporciona um resultado mais seguro e duradouro.',
      typesTitle: 'Tipos de Abdominoplastia',
      typeComplete: 'Abdominoplastia Completa',
      typeCompleteDesc: 'Indicada quando há um excesso significativo de pele e gordura. É realizada uma incisão de quadril a quadril, elimina-se o excesso de tecido e reposiciona-se o umbigo.',
      typeMini: 'Miniabdominoplastia',
      typeMiniDesc: 'Para casos com menor excesso de pele, principalmente na zona inferior do abdômen. A cicatriz é mais curta e a recuperação mais rápida.',
      typeLipo: 'Abdominoplastia com Lipoaspiração',
      typeLipoDesc: 'Combina a eliminação de pele com a extração de gordura localizada para conseguir um contorno mais definido.',
      benefitsTitle: 'Benefícios da Abdominoplastia',
      benefit1: 'Barriga Plana',
      benefit1Desc: 'Eliminação do excesso de pele e gordura para um abdômen firme e tonificado.',
      benefit2: 'Melhora Postural',
      benefit2Desc: 'A reparação dos músculos abdominais melhora a postura e reduz dores nas costas.',
      benefit3: 'Resultados Duradouros',
      benefit3Desc: 'Com um estilo de vida saudável, os resultados se mantêm a longo prazo.',
      benefit4: 'Maior Confiança',
      benefit4Desc: 'Melhora significativa da autoestima e da imagem corporal.',
      expTitle: 'Experiência em Cirurgia Corporal',
      expP1: 'O Dr. Ángel Norro conta com mais de <strong>30 anos de experiência</strong> em cirurgia estética corporal, realizando centenas de abdominoplastias com excelentes resultados.',
      expP2: 'Cada paciente recebe um <strong>estudo personalizado</strong> para determinar a técnica mais adequada segundo suas características e objetivos.',
      expP3: 'O objetivo é conseguir um resultado <strong>natural e harmonioso</strong> que melhore a silhueta respeitando as proporções de cada corpo.',
      contactTitle: 'Interessada em Abdominoplastia?',
      contactDesc: 'Solicite uma avaliação personalizada',
    },
  }[lang] || t.es;
  
  const texts = t;

  // Image groups for lightbox
  const case1Imgs = [
    { src: '/images/abdominoplastia-caso2-pre-frontal.jpg', alt: texts.before, caption: `${texts.before} - Frontal` },
    { src: '/images/abdominoplastia-caso2-pre-lateral.jpg', alt: texts.before, caption: `${texts.before} - Lateral` },
    { src: '/images/abdominoplastia-caso2-pre-frontal2.jpg', alt: texts.before, caption: `${texts.before} - Frontal 2` },
    { src: '/images/abdominoplastia-caso2-pre-lateral2.jpg', alt: texts.before, caption: `${texts.before} - Lateral 2` },
    { src: '/images/abdominoplastia-caso2-post-30dias.jpg', alt: texts.days30, caption: texts.days30 },
    { src: '/images/abdominoplastia-caso2-post-90dias.jpg', alt: texts.days90, caption: texts.days90 },
    { src: '/images/abdominoplastia-caso2-post-90dias-frontal.jpg', alt: texts.days90, caption: `${texts.days90} - Frontal` },
    { src: '/images/abdominoplastia-caso2-post-cicatriz-90dias.jpg', alt: texts.scar90, caption: texts.scar90 },
    { src: '/images/abdominoplastia-caso2-post-sentada-90dias.jpg', alt: texts.relaxed90, caption: texts.relaxed90 },
    { src: '/images/abdominoplastia-caso2-post-sentada.jpg', alt: texts.finalResult, caption: texts.finalResult },
  ];
  const case2Imgs = [
    { src: '/images/abdominoplastia-caso3-pre.jpg', alt: texts.before, caption: texts.before },
    { src: '/images/abdominoplastia-caso3-post-1ano.jpg', alt: texts.oneYear, caption: texts.oneYear },
    { src: '/images/abdominoplastia-caso3-cicatriz-1ano.jpg', alt: texts.scarYear, caption: texts.scarYear },
  ];
  const case3Imgs = [
    { src: '/images/cirugia-combinada-pre.jpg', alt: texts.before, caption: texts.before },
    { src: '/images/cirugia-combinada-post-1mes.jpg', alt: texts.oneMonth, caption: texts.oneMonth },
  ];
  
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#B8975A] to-[#8b7355]"></div>
        
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-8">
            <a href="tel:952764547" className="text-[#8b7355] font-semibold hover:underline">
              952 76 45 47
            </a>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-[#1C1C1A] text-center mb-6 leading-tight">
            {texts.title}
          </h1>
          
          <p className="text-lg md:text-xl text-text-secondary text-center mb-4 max-w-3xl mx-auto" dangerouslySetInnerHTML={{ __html: texts.heroP1 }} />
          
          <p className="text-lg text-text-secondary text-center mb-8">
            {texts.heroP2}
          </p>
          
          <div className="text-center">
            <Button 
              onClick={() => scrollToSection('contacto')}
              className="bg-[#B8975A] hover:bg-[#a67259] text-white px-8 py-6 text-lg rounded transition-all duration-300"
            >
              {texts.cta}
            </Button>
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="py-12 bg-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-light text-[#1C1C1A] text-center mb-4">
              {texts.resultsTitle}
            </h2>
            <p className="text-center text-text-secondary mb-8">
              {texts.resultsSubtitle}
            </p>
            
            {/* Caso 1 */}
            <div className="mb-16">
              <h3 className="text-xl font-medium text-[#1C1C1A] text-center mb-6">
                {texts.case1Title}
              </h3>
              <p className="text-center text-text-secondary mb-6 max-w-2xl mx-auto">
                {texts.case1Desc}
              </p>
              
              {/* Grid 2x2 - Antes */}
              <div className="max-w-4xl mx-auto mb-8">
                <p className="text-center text-text-secondary font-medium mb-4">{texts.before}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {case1Imgs.slice(0, 4).map((img, i) => (
                    <div key={i} className="aspect-[3/4] overflow-hidden rounded-none shadow-lg cursor-pointer group relative" onClick={() => openLb(case1Imgs, i)}>
                      <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-center" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                        <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={28} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Grid - Después */}
              <div className="max-w-4xl mx-auto">
                <p className="text-center text-text-secondary font-medium mb-4">{texts.after}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {case1Imgs.slice(4, 8).map((img, i) => (
                    <div key={i} className="text-center">
                      <div className="aspect-[3/4] overflow-hidden rounded-none shadow-lg cursor-pointer group relative" onClick={() => openLb(case1Imgs, i + 4)}>
                        <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-center" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                          <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={28} />
                        </div>
                      </div>
                      <p className="mt-2 text-text-muted text-sm">{img.caption}</p>
                    </div>
                  ))}
                </div>
                
                {/* Fotos adicionales - Sentada */}
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                  {case1Imgs.slice(8).map((img, i) => (
                    <div key={i}>
                      <div className="aspect-square overflow-hidden rounded-none shadow-lg cursor-pointer group relative" onClick={() => openLb(case1Imgs, i + 8)}>
                        <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-center" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                          <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={28} />
                        </div>
                      </div>
                      <p className="mt-3 text-text-secondary text-sm text-center"><strong>{img.caption}</strong></p>
                    </div>
                  ))}
                </div>
                <p className="text-text-muted text-xs text-center mt-4 max-w-2xl mx-auto">
                  {texts.diastasisNote}
                </p>
              </div>
              
              <p className="text-center text-text-muted text-sm mt-6">{texts.disclaimer}</p>
            </div>
            
            {/* Caso 2 */}
            <div className="pt-12 border-t border-gray-200">
              <h3 className="text-xl font-medium text-[#1C1C1A] text-center mb-6">{texts.case2Title}</h3>
              <p className="text-center text-text-secondary mb-6 max-w-2xl mx-auto">{texts.case2Desc}</p>
              
              <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {case2Imgs.map((img, i) => (
                    <div key={i} className="text-center">
                      <div className="aspect-[3/4] overflow-hidden rounded-none shadow-lg cursor-pointer group relative" onClick={() => openLb(case2Imgs, i)}>
                        <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-center" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                          <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={28} />
                        </div>
                      </div>
                      <p className="mt-3 text-text-secondary font-medium">{img.caption}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <p className="text-center text-text-muted text-sm mt-6">{texts.disclaimer}</p>
            </div>
            
            {/* Caso 3 */}
            <div className="pt-12 border-t border-gray-200">
              <h3 className="text-xl font-medium text-[#1C1C1A] text-center mb-6">{texts.case3Title}</h3>
              <p className="text-center text-text-secondary mb-4 max-w-2xl mx-auto">{texts.case3Desc}</p>
              <p className="text-center text-text-muted text-sm mb-8 max-w-2xl mx-auto"><em>{texts.case3Note}</em></p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {case3Imgs.map((img, i) => (
                  <div key={i} className="text-center">
                    <div className="aspect-[3/4] overflow-hidden rounded-none shadow-lg cursor-pointer group relative" onClick={() => openLb(case3Imgs, i)}>
                      <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-center" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                        <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={28} />
                      </div>
                    </div>
                    <p className="mt-3 text-text-secondary font-medium">{i === 0 ? texts.before : texts.oneMonth}</p>
                  </div>
                ))}
              </div>
              
              <p className="text-center text-text-muted text-sm mt-6">{texts.disclaimer}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-8">{texts.whatIsTitle}</h2>
          <div className="prose prose-lg max-w-none text-text-secondary">
            <p className="mb-6" dangerouslySetInnerHTML={{ __html: texts.whatIsP1 }} />
            <p className="mb-6">{texts.whatIsP2}</p>
          </div>
        </div>
      </section>

      {/* Cirugía de Pared Abdominal Section */}
      <section className="py-20 bg-ivory px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-8">{texts.wallTitle}</h2>
          <div className="prose prose-lg max-w-none text-text-secondary mb-12">
            <p className="mb-6" dangerouslySetInnerHTML={{ __html: texts.wallP1 }} />
          </div>
          
          <div className="space-y-8">
            <div className="bg-cream rounded-none p-6">
              <h3 className="text-xl font-semibold text-[#1C1C1A] mb-3">{texts.diastasisTitle}</h3>
              <p className="text-text-secondary mb-4" dangerouslySetInnerHTML={{ __html: texts.diastasisP1 }} />
              <ul className="list-disc list-inside text-text-secondary space-y-2">
                {texts.diastasisList.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <p className="text-text-secondary mt-4">{texts.diastasisP2}</p>
            </div>
            
            <div className="bg-cream rounded-none p-6">
              <h3 className="text-xl font-semibold text-[#1C1C1A] mb-3">{texts.herniaTitle}</h3>
              <p className="text-text-secondary mb-4" dangerouslySetInnerHTML={{ __html: texts.herniaP1 }} />
              <p className="text-text-secondary">{texts.herniaP2}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Types Section */}
      <section className="py-20 bg-cream px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-8">{texts.typesTitle}</h2>
          <div className="space-y-8">
            <div className="bg-ivory rounded-none p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-[#1C1C1A] mb-3">{texts.typeComplete}</h3>
              <p className="text-text-secondary">{texts.typeCompleteDesc}</p>
            </div>
            <div className="bg-ivory rounded-none p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-[#1C1C1A] mb-3">{texts.typeMini}</h3>
              <p className="text-text-secondary">{texts.typeMiniDesc}</p>
            </div>
            <div className="bg-ivory rounded-none p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-[#1C1C1A] mb-3">{texts.typeLipo}</h3>
              <p className="text-text-secondary">{texts.typeLipoDesc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-[#1C1C1A] px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-8 text-center">{texts.benefitsTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-ivory/10 backdrop-blur-sm rounded-none p-6">
              <Check className="text-[#d4af37] mb-3" size={24} />
              <h3 className="text-white font-semibold mb-2">{texts.benefit1}</h3>
              <p className="text-white/80">{texts.benefit1Desc}</p>
            </div>
            <div className="bg-ivory/10 backdrop-blur-sm rounded-none p-6">
              <Check className="text-[#d4af37] mb-3" size={24} />
              <h3 className="text-white font-semibold mb-2">{texts.benefit2}</h3>
              <p className="text-white/80">{texts.benefit2Desc}</p>
            </div>
            <div className="bg-ivory/10 backdrop-blur-sm rounded-none p-6">
              <Check className="text-[#d4af37] mb-3" size={24} />
              <h3 className="text-white font-semibold mb-2">{texts.benefit3}</h3>
              <p className="text-white/80">{texts.benefit3Desc}</p>
            </div>
            <div className="bg-ivory/10 backdrop-blur-sm rounded-none p-6">
              <Check className="text-[#d4af37] mb-3" size={24} />
              <h3 className="text-white font-semibold mb-2">{texts.benefit4}</h3>
              <p className="text-white/80">{texts.benefit4Desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Doctor Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/3">
              <img src="/images/foto-angel-bata.webp" alt="Dr. Ángel Norro" className="w-full h-auto rounded-none shadow-lg" />
            </div>
            <div className="lg:w-2/3">
              <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-6">{texts.expTitle}</h2>
              <p className="text-text-secondary mb-4" dangerouslySetInnerHTML={{ __html: texts.expP1 }} />
              <p className="text-text-secondary mb-4" dangerouslySetInnerHTML={{ __html: texts.expP2 }} />
              <p className="text-text-secondary" dangerouslySetInnerHTML={{ __html: texts.expP3 }} />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-20 bg-[#B8975A] px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">{texts.contactTitle}</h2>
          <p className="text-white/90 mb-8 text-lg">{texts.contactDesc}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:952764547" className="inline-flex items-center justify-center gap-2 bg-ivory text-[#B8975A] px-8 py-4 rounded-none font-semibold hover:bg-gray-100 transition-colors">
              <Phone size={20} />
              952 76 45 47
            </a>
            <a href="https://wa.me/34696345792" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-gold500 text-white px-8 py-4 rounded-none font-semibold hover:bg-gold600 transition-colors">
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />

      {/* Lightbox */}
      {lbIndex !== null && (
        <Lightbox
          images={lbImages}
          currentIndex={lbIndex}
          onClose={() => setLbIndex(null)}
          onPrev={() => setLbIndex(i => Math.max(0, i - 1))}
          onNext={() => setLbIndex(i => Math.min(lbImages.length - 1, i + 1))}
        />
      )}
    </div>
  );
};

export default CirugiaAbdomen;
